import './boot'; // MUST BE FIRST
import React from 'react'
import ReactDOM from 'react-dom/client'
import { createPortal } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import './index.css';

// Static Imports for Providers to avoid Context evaluation race conditions
import App from './App';
import { ThemeProvider } from './components/ThemeProvider';
import { CriticalErrorBoundary } from './components/CriticalErrorBoundary';
import { Toaster } from './components/ui/sonner';
import { YapInteractionsProvider } from './components/YapInteractionsContext';
import { NotificationProvider } from './components/NotificationContext';
import { SettingsProvider } from './components/SettingsContext';
import { UserDataProvider } from './components/UserDataContext';
import { MessagingProvider } from './components/MessagingContext';
import { MetricDeltaProvider } from './components/MetricDeltaContext';
import { AuthGateProvider } from './contexts/AuthGateContext';
import { AnalyticsService } from './services/AnalyticsService';
import { loadLocalFonts } from './utils/loadLocalFonts';
import { MetaService } from './services/MetaService';

const debugLog = (msg: string) => {
    // console.log(`[BOOT-TRAP] ${msg}`);
};

window.onerror = (msg, url, lineNo, columnNo, error) => {
    debugLog(`ERR: ${msg} @ ${url}:${lineNo}:${columnNo}`);
    if (error && error.stack) debugLog(`STACK: ${error.stack}`);
    return false;
};

// A deploy replaces every hashed chunk, so a tab opened before it asks for
// files that no longer exist ("Failed to fetch dynamically imported module").
// Reload once to pick up the new index.html. The timestamp guard stops a loop
// if the chunk is missing for some other reason.
window.addEventListener('vite:preloadError', (event) => {
    const KEY = 'afroyap:chunkReloadAt';
    let last = 0;
    try { last = Number(sessionStorage.getItem(KEY) || 0); } catch {}
    if (Date.now() - last < 30_000) return; // already tried: let the error surface
    try { sessionStorage.setItem(KEY, String(Date.now())); } catch {}
    event.preventDefault();
    window.location.reload();
});

window.addEventListener('unhandledrejection', (event) => {
    debugLog(`UHR: ${event.reason}`);
    if (event.reason && event.reason.stack) debugLog(`STACK: ${event.reason.stack}`);
});

const startApp = async () => {
    try {
        debugLog('Starting bootstrap...');
        
        // Force-load self-hosted display fonts (AmericanCaptain etc.) with retry
        // so they reliably appear on mobile instead of being stuck on the
        // fallback when the lazy CSS fetch is slow/dropped.
        loadLocalFonts();

        // 📊 ANALYTICS: Start tracking
        AnalyticsService.init();
        // Loaded at boot so the _fbp cookie exists before anything is tracked;
        // a signup fired before the Pixel has run would carry no attribution.
        // No-ops on native and when no Pixel ID is configured.
        MetaService.init();
        
        // Render with stable static providers
        ReactDOM.createRoot(document.getElementById('root')!).render(
            <React.StrictMode>
                <BrowserRouter>
                    <ThemeProvider>
                        <SettingsProvider>
                            <CriticalErrorBoundary>
                                <MetricDeltaProvider>
                                    <UserDataProvider>
                                        <NotificationProvider>
                                            <MessagingProvider>
                                                <YapInteractionsProvider>
                                                    <AuthGateProvider>
                                                        <App />
                                                    </AuthGateProvider>
                                                    {/* NUCLEAR TOASTER FIX 3.0: Portal to a dedicated element OUTSIDE the app stack and AFTER all other portals */}
                                                    {typeof document !== 'undefined' && document.getElementById('toast-portal') && createPortal(<Toaster />, document.getElementById('toast-portal')!)}
                                                </YapInteractionsProvider>
                                            </MessagingProvider>
                                        </NotificationProvider>
                                    </UserDataProvider>
                                </MetricDeltaProvider>
                            </CriticalErrorBoundary>
                        </SettingsProvider>
                    </ThemeProvider>
                </BrowserRouter>
            </React.StrictMode>
        );

    } catch (e: any) {
        console.error('[BOOT] CRITICAL LOAD FAILURE:', e);
        debugLog(`CRITICAL BOOT FAIL:\n${e.message}\n${e.stack}`);

        // Visual Fallback
        document.body.innerHTML = `
            <div style="padding:20px; color:red; font-family:sans-serif;">
                <h1>App Failed to Load</h1>
                <p><b>Error:</b> ${e.message}</p>
                <pre>${e.stack}</pre>
            </div>
        `;
    }
};

startApp();
